import { getByPath, parse } from '@de/utils/interpolation';
import { FORBIDDEN_KEYS } from '@de/utils/interpolation/forbiddenKeys';
import type { Expression, Literal } from '@de/utils/interpolation/types';
import type { FormState } from '@de/providers/DataSourceManager.types';
import { warnOnce } from '@de/utils/warnOnce';

const WARN_ONCE_NAMESPACE = 'lead-submit-values';

/** The only paths a `lead.submit` `lead`, `attributes` or `conversionId` template may read. */
export const LEAD_SUBMIT_VALUE_ROOTS = ['context.params', 'formState'] as const;

/** The only paths a `lead.submit` `organizationId` or `offeringId` template may read. */
export const LEAD_SUBMIT_ROUTING_ROOTS = ['context.params'] as const;

export interface LeadSubmitValueScope {
  readonly contextParams: unknown;
  readonly formState: FormState;
}

const REFUSED = Symbol('refused');
const UNRESOLVED = Symbol('unresolved');

/**
 * Resolves a flat object of `lead.submit` values: the `lead` or `attributes`
 * object, or the routing params. Only paths below `roots` can be read
 * (`context.params.<key>` and `formState.<key>` by default). A value is omitted
 * when it is not a string, reads anything else, or resolves to nothing; a
 * mixed template with any unresolved expression is omitted whole.
 */
export function resolveLeadSubmitValues(
  raw: unknown,
  scope: LeadSubmitValueScope,
  roots: ReadonlyArray<string> = LEAD_SUBMIT_VALUE_ROOTS,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return out;
  }
  const readable = {
    context: { params: scope.contextParams },
    formState: scope.formState,
  };
  for (const [key, value] of Object.entries(raw)) {
    if (FORBIDDEN_KEYS.has(key) || typeof value !== 'string') continue;
    const resolved = resolveValue(key, value, readable, roots);
    if (resolved !== undefined) out[key] = resolved;
  }
  return out;
}

function resolveValue(
  key: string,
  template: string,
  readable: unknown,
  roots: ReadonlyArray<string>,
): string | undefined {
  if (!template.includes('{{')) {
    return nonEmpty(template);
  }
  let segments: ReadonlyArray<Literal | Expression>;
  try {
    segments = parse(template).segments;
  } catch {
    warnRefused(key, 'unparseable template');
    return undefined;
  }

  let out = '';
  for (const segment of segments) {
    if (segment.type === 'literal') {
      out += segment.value;
      continue;
    }
    const piece = resolveExpression(segment, readable, roots);
    if (piece === REFUSED) {
      warnRefused(key, rootOf(segment));
      return undefined;
    }
    if (piece === UNRESOLVED) return undefined;
    out += piece;
  }
  return nonEmpty(out);
}

function resolveExpression(
  expression: Expression,
  readable: unknown,
  roots: ReadonlyArray<string>,
): string | typeof REFUSED | typeof UNRESOLVED {
  if (!readsAllowedRoot(expression, roots)) return REFUSED;
  return toScalarString(getByPath(readable, expression.segments));
}

/** True when the path is a key below one of `roots`, never the root itself. */
function readsAllowedRoot(
  expression: Expression,
  roots: ReadonlyArray<string>,
): boolean {
  if (expression.kind !== 'path') return false;
  const { segments } = expression;
  if (segments.some((segment) => segment.type !== 'key')) return false;
  const names = segments.map((segment) =>
    segment.type === 'key' ? segment.name : '',
  );
  return roots.some((root) => {
    const rootNames = root.split('.');
    return (
      names.length > rootNames.length &&
      rootNames.every((rootName, index) => names[index] === rootName)
    );
  });
}

function toScalarString(value: unknown): string | typeof UNRESOLVED {
  const single = scalarToString(value);
  if (single !== undefined) return nonEmpty(single) ?? UNRESOLVED;
  if (Array.isArray(value) && value.length > 0) {
    const parts = value.map(scalarToString);
    if (parts.every((part): part is string => part !== undefined)) {
      return nonEmpty(parts.join(',')) ?? UNRESOLVED;
    }
  }
  return UNRESOLVED;
}

function scalarToString(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return String(value);
  return undefined;
}

function nonEmpty(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function rootOf(expression: Expression): string {
  if (expression.kind !== 'path') return 'function call';
  const first = expression.segments[0];
  return first?.type === 'key' ? first.name : 'path';
}

// Names the key and root only. The value is never read for a refused template.
function warnRefused(key: string, root: string): void {
  warnOnce(
    WARN_ONCE_NAMESPACE,
    `[lead.submit] Omitting "${key}": its template reads "${root}", which this value cannot read`,
  );
}
